import React from 'react';
import Alert from '../../../shared/components/Alert/Alert';
import { isQuoteRevision } from '../quoteRevision';

// Read-only guidance for the existing server promotion guard. Never convert Ens.
export const legacyEnsLines = editor => {
  const found = [];
  const visit = (sections, scope) => (sections || []).forEach(section => {
    (section.lines || []).forEach(line => {
      if (line.unit === 'Ens' && (!line.structureKind || line.structureKind === 'simple'))
        found.push({ ...line, location: [scope, section.title].filter(Boolean).join(' · ') });
    });
    visit(section.children, scope);
  });
  visit(editor.sections, 'Devis');
  (editor.options || []).forEach(option => visit(option.sections, `Option ${option.title || ''}`));
  return found;
};

export const compositionPromotionState = (editor, access) => {
  if (access.status === 'loading') return 'Vérification de l’accès aux ensembles composés…';
  if (access.status !== 'ready') return 'Impossible de vérifier l’accès V2. Actualisez l’accès pour réessayer ; vos saisies sont conservées.';
  if (!editor.id || !editor.quoteVersionId) return 'Enregistrez d’abord ce nouveau devis, puis activez explicitement les ensembles composés.';
  if (editor.versionStatus !== 'draft') return 'Cette version est figée. La composition ne peut être activée que dans un brouillon modifiable.';
  if (editor.commercialStatus === 'archived') return 'Ce dossier est archivé : son contenu ne peut pas être converti.';
  if (editor.currentVersionId && String(editor.currentVersionId) !== String(editor.quoteVersionId)) return 'Seul le brouillon courant du dossier peut être converti.';
  if ((editor.currencyCode || 'EUR') !== 'EUR') return 'Ce devis utilise une devise historique différente de EUR : il reste en lecture seule.';
  if (!editor.permissions?.canEdit) return 'Vous ne disposez pas de l’autorisation de modifier ce brouillon.';
  if (!access.permissions.canUseComposition) return access.permissions.canEditDraft === false
    ? 'Votre compte ne dispose pas de l’autorisation de modifier les brouillons.'
    : 'Les ensembles composés ne sont pas activés pour votre accès actuel. Actualisez l’accès après leur activation.';
  if (!editor.permissions.canViewCosts || access.permissions.canViewCosts === false) return 'La permission de consulter les coûts est nécessaire pour confirmer les coûts lors de l’activation.';
  if (!isQuoteRevision(editor.revision)) return 'La révision du brouillon est absente ou invalide. Rechargez sa version actuelle avant l’activation.';
  if (editor.serverCalculationStatus === 'stale' || editor.serverWriteConflict) return 'Enregistrez vos modifications V1 ou résolvez le conflit de version avant d’activer les ensembles composés.';
  if (legacyEnsLines(editor).length) return 'Activation bloquée : des prestations simples utilisent l’unité Ens, qui ne permet pas de déterminer leur structure.';
  return null;
};

export default function CompositionPromotionNotice({ editor, access, saving, onEnable }) {
  if (Number(editor.contentSchemaVersion) === 2) return null;
  const reason = compositionPromotionState(editor, access), ambiguous = legacyEnsLines(editor);
  return <Alert severity={reason ? 'warning' : 'info'}>
    <section aria-label="Accès aux ensembles composés">
      <strong>Ensembles composés</strong>
      <p role="status">{reason || 'Ce brouillon utilise le chiffrage V1. Activez explicitement les ensembles composés pour créer un ensemble et lui ajouter une prestation enfant.'}</p>
      {ambiguous.length > 0 && <>
        <p>Prestations à examiner dans le détail V1 :</p>
        <ul>{ambiguous.map((line, index) => <li key={`${line.location}:${line.id}:${index}`}>{line.description || 'Prestation sans désignation'} — {line.location} — unité Ens</li>)}</ul>
        <p>Si la prestation est simple, corrigez son unité réelle dans le panneau de détail V1, puis enregistrez. Si Ens est intentionnel, ne remplacez pas arbitrairement cette unité : clarifiez d’abord la structure souhaitée. Aucune ligne, aucun coût et aucune quantité ne sont convertis automatiquement.</p>
      </>}
      <button className="basic-button" type="button" disabled={saving || !!reason} onClick={() => { if (!reason) onEnable(); }}>Activer les ensembles composés</button>
      {' '}<button className="basic-button" type="button" disabled={access.status === 'loading'} onClick={access.onRefresh}>Actualiser l’accès V2</button>
    </section>
  </Alert>;
}
